import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import {
  getProyectosAsignables,
  getUsuariosAsignados,
  quitarUsuarioProyecto,
} from "./usuariosProyectosApi";
import {
  buildAsignacionesGenerales,
  canAccessUsuariosProyectos,
  filterAsignacionesGenerales,
  getConteoProyectosPorUsuario,
  getProyectoId,
  getProyectoLabel,
  getResumenAsignaciones,
  getStoredUsuariosProyectosUser,
  getUsuarioId,
  getUsuariosProyectosErrorMessage,
} from "./usuariosProyectosUtils";
import QuitarUsuarioDialog from "./components/QuitarUsuarioDialog";
import UsuariosProyectoManager from "./components/UsuariosProyectoManager";
import UsuariosProyectoTable from "./components/UsuariosProyectoTable";

export const UsuariosProyectosRouteGuard = ({ children }) =>
  canAccessUsuariosProyectos(getStoredUsuariosProyectosUser()) ? (
    children
  ) : (
    <Navigate to="/accessDenied" replace />
  );

const INITIAL_GLOBAL_FILTERS = {
  projectId: "",
  role: "",
  status: "",
  search: "",
};

const UsuariosProyectos = () => {
  const theme = useTheme();
  const [tab, setTab] = useState("project");
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [projectsError, setProjectsError] = useState("");
  const [globalResults, setGlobalResults] = useState({});
  const [globalFailures, setGlobalFailures] = useState([]);
  const [globalLoaded, setGlobalLoaded] = useState(false);
  const [isLoadingGlobal, setIsLoadingGlobal] = useState(false);
  const [globalProgress, setGlobalProgress] = useState({ current: 0, total: 0 });
  const [globalFilters, setGlobalFilters] = useState(INITIAL_GLOBAL_FILTERS);
  const [userToRemove, setUserToRemove] = useState(null);
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [unassignError, setUnassignError] = useState("");
  const [snackbar, setSnackbar] = useState("");
  const globalSequence = useRef(0);

  const loadProjects = useCallback(async () => {
    setIsLoadingProjects(true);
    setProjectsError("");
    try {
      setProjects(await getProyectosAsignables());
    } catch (error) {
      setProjects([]);
      setProjectsError(getUsuariosProyectosErrorMessage(error, "projects"));
    } finally {
      setIsLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
    return () => {
      globalSequence.current += 1;
    };
  }, [loadProjects]);

  const loadGlobal = useCallback(async (projectsToLoad, preserveResults = false) => {
    const sequence = ++globalSequence.current;
    const targetProjects = Array.isArray(projectsToLoad) ? projectsToLoad : [];
    const accumulated = preserveResults ? { ...globalResults } : {};
    const failures = [];

    if (!preserveResults) setGlobalResults({});
    setGlobalFailures([]);
    setGlobalLoaded(true);
    setIsLoadingGlobal(true);
    setGlobalProgress({ current: 0, total: targetProjects.length });

    for (let index = 0; index < targetProjects.length; index += 1) {
      const project = targetProjects[index];
      const projectId = getProyectoId(project);
      try {
        const users = await getUsuariosAsignados(projectId);
        if (sequence !== globalSequence.current) return;
        accumulated[String(projectId)] = { project: { ...project }, users: [...users] };
        setGlobalResults({ ...accumulated });
      } catch (error) {
        if (sequence !== globalSequence.current) return;
        failures.push(project);
      }
      setGlobalProgress({ current: index + 1, total: targetProjects.length });
    }

    if (sequence === globalSequence.current) {
      setGlobalFailures(failures);
      setIsLoadingGlobal(false);
    }
  }, [globalResults]);

  const globalRows = useMemo(
    () => buildAsignacionesGenerales(globalResults),
    [globalResults]
  );
  const filteredGlobalRows = useMemo(
    () => filterAsignacionesGenerales(globalRows, globalFilters),
    [globalFilters, globalRows]
  );
  const summary = useMemo(() => getResumenAsignaciones(globalRows), [globalRows]);
  const projectCounts = useMemo(
    () => getConteoProyectosPorUsuario(globalRows),
    [globalRows]
  );
  const roleOptions = useMemo(
    () => Array.from(new Set(globalRows.map((row) => String(row.nombreRol || row.rol || "")).filter(Boolean))).sort(),
    [globalRows]
  );

  const updateGlobalFilter = (key, value) => {
    setGlobalFilters((current) => ({ ...current, [key]: value }));
  };

  const handleUnassign = async () => {
    if (!userToRemove) return;
    setIsUnassigning(true);
    setUnassignError("");
    try {
      await quitarUsuarioProyecto(userToRemove.idProyecto, getUsuarioId(userToRemove));
      const project = projects.find(
        (item) => String(getProyectoId(item)) === String(userToRemove.idProyecto)
      );
      const users = await getUsuariosAsignados(userToRemove.idProyecto);
      setGlobalResults((current) => ({
        ...current,
        [String(userToRemove.idProyecto)]: { project, users },
      }));
      setUserToRemove(null);
      setSnackbar("Usuario retirado del proyecto.");
    } catch (error) {
      setUnassignError(getUsuariosProyectosErrorMessage(error, "unassign"));
    } finally {
      setIsUnassigning(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <ManageAccountsOutlinedIcon color="primary" />
        <Typography variant="h3" fontWeight={800}>
          Asignación de usuarios a proyectos
        </Typography>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Administra qué usuarios tienen acceso a cada proyecto.
      </Typography>

      <Paper
        data-testid="uap-main-paper"
        elevation={4}
        sx={{
          width: "100%",
          bgcolor: theme.palette.background.alt,
          borderRadius: 1,
          p: { xs: 2, sm: 3 },
        }}
      >
        {projectsError && (
          <Alert severity="error" action={<Button color="inherit" onClick={loadProjects}>Reintentar</Button>} sx={{ mb: 2 }}>
            {projectsError}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={tab} onChange={(event, value) => setTab(value)}>
            <Tab label="Por proyecto" value="project" />
            <Tab label="Vista general" value="global" />
          </Tabs>
        </Box>

        {tab === "project" && (
          <Box>
          <Autocomplete
            options={projects}
            value={selectedProject}
            onChange={(event, value) => setSelectedProject(value)}
            loading={isLoadingProjects}
            getOptionLabel={getProyectoLabel}
            isOptionEqualToValue={(option, value) =>
              String(getProyectoId(option)) === String(getProyectoId(value))
            }
            sx={{ maxWidth: 620, mb: 3 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Proyecto"
                placeholder="Seleccionar proyecto"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingProjects && <CircularProgress color="inherit" size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {!selectedProject ? (
            <Alert severity="info">
              Selecciona un proyecto para consultar sus usuarios asignados.
            </Alert>
          ) : (
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
                Usuarios asignados
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {getProyectoLabel(selectedProject)}
              </Typography>
              <UsuariosProyectoManager project={selectedProject} />
            </Box>
          )}
          </Box>
        )}

        {tab === "global" && (
          <Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h5" fontWeight={800}>Vista general de asignaciones</Typography>
              <Typography variant="body2" color="text.secondary">
                Esta consulta consolida las asignaciones de todos los proyectos activos.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<RefreshOutlinedIcon />}
              onClick={() => loadGlobal(projects, false)}
              disabled={isLoadingGlobal || isLoadingProjects}
            >
              Cargar vista general
            </Button>
          </Stack>

          {isLoadingGlobal && (
            <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 2 }}>
              Cargando proyecto {globalProgress.current} de {globalProgress.total}...
            </Alert>
          )}
          {globalFailures.length > 0 && (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" onClick={() => loadGlobal(globalFailures, true)} disabled={isLoadingGlobal}>
                  Reintentar pendientes
                </Button>
              }
              sx={{ mb: 2 }}
            >
              No fue posible cargar {globalFailures.length} proyecto{globalFailures.length === 1 ? "" : "s"}.
            </Alert>
          )}

          {!globalLoaded ? (
            <Alert severity="info">Pulsa “Cargar vista general” para iniciar la consulta.</Alert>
          ) : (
            <>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {[
                  ["Proyectos con asignaciones", summary.proyectos],
                  ["Usuarios asignados", summary.usuarios],
                  ["Asignaciones activas", summary.asignaciones],
                  ["Usuarios con más de un proyecto", summary.multiproyecto],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} lg={3} key={label}>
                    <Paper
                      variant="outlined"
                      sx={{
                        bgcolor: theme.palette.background.alt,
                        p: 2,
                        borderRadius: 1,
                        height: "100%",
                      }}
                    >
                      <Typography variant="h4" fontWeight={800}>{value}</Typography>
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label="Proyecto"
                    value={globalFilters.projectId}
                    onChange={(event) => updateGlobalFilter("projectId", event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {projects.map((project) => (
                      <MenuItem key={getProyectoId(project)} value={String(getProyectoId(project))}>
                        {getProyectoLabel(project)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label="Rol"
                    value={globalFilters.role}
                    onChange={(event) => updateGlobalFilter("role", event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {roleOptions.map((role) => <MenuItem key={role} value={role}>{role}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label="Estado"
                    value={globalFilters.status}
                    onChange={(event) => updateGlobalFilter("status", event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="active">Activo</MenuItem>
                    <MenuItem value="inactive">Inactivo</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Buscar usuario"
                    value={globalFilters.search}
                    onChange={(event) => updateGlobalFilter("search", event.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>

              <UsuariosProyectoTable
                rows={filteredGlobalRows}
                onRemove={setUserToRemove}
                showProject
                projectCounts={projectCounts}
                paginated
              />
            </>
          )}
          </Box>
        )}
      </Paper>

      <QuitarUsuarioDialog
        open={Boolean(userToRemove)}
        project={userToRemove}
        user={userToRemove}
        loading={isUnassigning}
        error={unassignError}
        onClose={() => {
          setUserToRemove(null);
          setUnassignError("");
        }}
        onConfirm={handleUnassign}
      />
      <Snackbar open={Boolean(snackbar)} autoHideDuration={4000} onClose={() => setSnackbar("")}>
        <Alert severity="success" variant="filled" onClose={() => setSnackbar("")}>
          {snackbar}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UsuariosProyectos;
